import React from "react";
import { toast } from "react-toastify";

import {
  KintoneExportedRecord,
  KintoneExportReport,
  KintoneExportSetting,
  KintoneFormFields,
  nullToUndefinedDeep,
  ProcedureType,
} from "@graffer-inc/cloud-gov-core";
import {
  gqlClient,
  gqlHeader,
  reportUnexpectedError,
  runGqlQueryV2,
  UpdateKintoneExportSettingInput,
} from "@graffer-inc/smart-apply";

import apiClient from "../../../../../../application/apis/api-client";
import { parseError } from "../../../../../../application/domain/kintone-export";
import {
  DefaultNumItemsPerPages,
  toTotalPage,
} from "../../../../../../application/domain/pagination/paging";
import {
  ManualExportSubmissionFuncType,
  useManualExportSubmission,
} from "../../../../../organisms/kintone-export/common-hook";

type UseKintoneExportSettingDetailPageReturnType = {
  pageContent?: PageContent;
  updateKintoneExportSetting: UpdateKintoneExportSettingFuncType;
  deleteKintoneExportedRecord: DeleteKintoneExportedRecordFuncType;

  currentExportedRecordsPage: number;
  setCurrentExportedRecordsPage: (page: number) => void;
  lastExportedRecordsPage: number;
  currentExportReportsPage: number;
  setCurrentExportReportsPage: (page: number) => void;
  lastExportReportsPage: number;

  manualExportSubmission: ManualExportSubmissionFuncType;
  newReport: Pick<KintoneExportReport, "id"> | undefined;
};

type PageContent = {
  kintoneExportSetting: KintoneExportSetting;
  kintoneExportedRecords: KintoneExportedRecord[];
  kintoneExportReports: Pick<
    KintoneExportReport,
    "id" | "createdAtUnixSecond" | "startedAtUnixSecond"
  >[];
  procedure: ProcedureType;
};

export type UpdateKintoneExportSettingFuncType = (
  param: UpdateKintoneExportSettingInput,
  callback: {
    onSucceeded: (newSetting: KintoneExportSetting) => void;
    onFailed: (errorMessage: string) => void;
  },
) => void;

export type DeleteKintoneExportedRecordFuncType = (
  param: {
    id: KintoneExportedRecord["id"];
  },
  callback: {
    onSucceeded: () => void;
    onFailed: (errorMessage: string) => void;
  },
) => void;

type HookProps<TENANT_ID extends KintoneExportSetting["tenantId"] | undefined> =
  {
    kintoneExportSettingId: KintoneExportSetting["id"];
    tenantId: TENANT_ID;
  };

export const useKintoneExportSettingDetailPage = <
  TENANT_ID extends KintoneExportSetting["tenantId"] | undefined,
>({
  kintoneExportSettingId,
  tenantId,
}: HookProps<TENANT_ID>): UseKintoneExportSettingDetailPageReturnType => {
  const [shouldRefetch, setShouldRefetch] = React.useState<boolean>(false);

  const {
    isLoading,
    data,
    updateKintoneExportSetting,
    currentExportedRecordsPage,
    setCurrentExportedRecordsPage,
    lastExportedRecordsPage,
    currentExportReportsPage,
    setCurrentExportReportsPage,
    lastExportReportsPage,
  } = useKintoneExportSettingDetail({
    kintoneExportSettingId,
    tenantId,
    shouldRefetch,
  });

  const { newReport, manualExportSubmission } = useManualExportSubmission();
  const { deletedRecordId, deleteKintoneExportedRecord } =
    useDeleteKintoneExportedRecord();

  React.useEffect(() => {
    if (isLoading) {
      setShouldRefetch(false);
      return;
    }

    if (!data) {
      setShouldRefetch(true);
      return;
    }

    if (
      !!newReport &&
      data.kintoneExportReports.every(({ id }) => id !== newReport.id)
    ) {
      setShouldRefetch(true);
      return;
    }

    if (
      !!deletedRecordId &&
      data.kintoneExportedRecords.some(({ id }) => id === deletedRecordId)
    ) {
      setShouldRefetch(true);
      return;
    }
  }, [isLoading, setShouldRefetch, data, deletedRecordId, newReport]);

  return {
    pageContent: data,
    updateKintoneExportSetting,
    deleteKintoneExportedRecord,
    currentExportedRecordsPage,
    setCurrentExportedRecordsPage,
    lastExportedRecordsPage,
    currentExportReportsPage,
    setCurrentExportReportsPage,
    lastExportReportsPage,
    manualExportSubmission,
    newReport,
  };
};

const exportedRecordsNumPerPage = DefaultNumItemsPerPages;
const exportReportsNumPerPage = DefaultNumItemsPerPages;

const useKintoneExportSettingDetail = ({
  kintoneExportSettingId,
  tenantId,
  shouldRefetch,
}: {
  kintoneExportSettingId: KintoneExportSetting["id"];
  tenantId: KintoneExportSetting["tenantId"] | undefined;
  shouldRefetch: boolean;
}) => {
  const [isLoading, setLoading] = React.useState(false);
  const [kintoneExportSetting, setKintoneExportSetting] =
    React.useState<PageContent["kintoneExportSetting"]>();
  const [procedure, setProcedure] = React.useState<PageContent["procedure"]>();
  const [kintoneExportedRecords, setKintoneExportedRecords] = React.useState<
    PageContent["kintoneExportedRecords"]
  >([]);
  const [currentExportedRecordsPage, setCurrentExportedRecordsPage] =
    React.useState(1);
  const [lastExportedRecordsPage, setLastExportedRecordsPage] =
    React.useState(1);
  const [kintoneExportReports, setKintoneExportReports] = React.useState<
    PageContent["kintoneExportReports"]
  >([]);
  const [currentExportReportsPage, setCurrentExportReportsPage] =
    React.useState(1);
  const [lastExportReportsPage, setLastExportReportsPage] = React.useState(1);

  React.useEffect(() => {
    const fetchSetting = async () => {
      setLoading(true);
      try {
        const res = await runGqlQueryV2(() => {
          return gqlClient.kintoneExportSetting(
            {
              id: kintoneExportSettingId,
            },
            gqlHeader("q"),
          );
        });

        if (res.error) {
          reportUnexpectedError(res.error.rawError);
          toast.error("連携設定の取得に失敗しました。");
          return;
        }

        setKintoneExportSetting(
          nullToUndefinedDeep(res.data!.kintoneExportSetting),
        );

        const { data: procedure } = await apiClient.fetchProcedure(
          res.data!.kintoneExportSetting.formResourceId,
        );

        setProcedure(procedure);
      } catch (e) {
        reportUnexpectedError(e);
        toast.error("連携設定の取得に失敗しました。");
      } finally {
        setLoading(false);
      }
    };

    if (shouldRefetch) {
      fetchSetting();
    }
  }, [
    shouldRefetch,
    kintoneExportSettingId,
    setLoading,
    setKintoneExportSetting,
    setProcedure,
  ]);

  const fetchKintoneExportedRecords = React.useCallback(() => {
    if (!tenantId) {
      return;
    }

    runGqlQueryV2(() => {
      return gqlClient.kintoneExportedRecords(
        {
          tenantId,
          kintoneExportSettingId,
          page: {
            limit: exportedRecordsNumPerPage,
            offset:
              exportedRecordsNumPerPage * (currentExportedRecordsPage - 1),
          },
        },
        gqlHeader("q"),
      );
    })
      .then((res) => {
        if (res.error) {
          reportUnexpectedError(res.error.rawError);
          toast.error("連携済みデータの取得に失敗しました。");
          return;
        }

        setKintoneExportedRecords(
          res.data!.kintoneExportedRecordsV2.edges.map(({ node }) => {
            return node;
          }),
        );
        setLastExportedRecordsPage(
          toTotalPage(
            res.data!.kintoneExportedRecordsV2.totalCount,
            exportedRecordsNumPerPage,
          ),
        );
      })
      .catch((e) => {
        reportUnexpectedError(e);
        toast.error("連携済みデータの取得に失敗しました。");
      });
  }, [
    tenantId,
    kintoneExportSettingId,
    currentExportedRecordsPage,
    setKintoneExportedRecords,
    setLastExportedRecordsPage,
  ]);

  // shouldRefetch が true になったとき、および、currentExportedRecordsPage が変更されたときに実行する
  // 初回レンダリング時の重複実行を防ぐために useRef を用いている。
  React.useEffect(() => {
    if (shouldRefetch) {
      fetchKintoneExportedRecords();
    }
  }, [fetchKintoneExportedRecords, shouldRefetch]);
  const isFirstRenderForExportedRecords = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRenderForExportedRecords.current) {
      isFirstRenderForExportedRecords.current = false;
      return;
    }

    fetchKintoneExportedRecords();
  }, [
    isFirstRenderForExportedRecords,
    fetchKintoneExportedRecords,
    currentExportedRecordsPage,
  ]);

  const fetchKintoneExportReports = React.useCallback(() => {
    if (!tenantId) {
      return;
    }

    runGqlQueryV2(() => {
      return gqlClient.kintoneExportReports(
        {
          tenantId,
          kintoneExportSettingId,
          page: {
            limit: exportReportsNumPerPage,
            offset: exportReportsNumPerPage * (currentExportReportsPage - 1),
          },
        },
        gqlHeader("q"),
      );
    })
      .then((res) => {
        if (res.error) {
          reportUnexpectedError(res.error.rawError);
          toast.error("連携レポートの取得に失敗しました。");
          return;
        }

        setKintoneExportReports(
          res.data!.kintoneExportReportsV2.edges.map(({ node }) => {
            return node;
          }),
        );
        setLastExportReportsPage(
          toTotalPage(
            res.data!.kintoneExportReportsV2.totalCount,
            exportReportsNumPerPage,
          ),
        );
      })
      .catch((e) => {
        reportUnexpectedError(e);
        toast.error("連携レポートの取得に失敗しました。");
      });
  }, [
    tenantId,
    kintoneExportSettingId,
    currentExportReportsPage,
    setKintoneExportReports,
    setLastExportReportsPage,
  ]);

  // shouldRefetch が true になったとき、および、currentExportReportsPage が変更されたときに実行する
  // 初回レンダリング時の重複実行を防ぐために useRef を用いている。
  React.useEffect(() => {
    if (shouldRefetch) {
      fetchKintoneExportReports();
    }
  }, [fetchKintoneExportReports, shouldRefetch]);
  const isFirstRenderForExportReports = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRenderForExportReports.current) {
      isFirstRenderForExportReports.current = false;
      return;
    }
    fetchKintoneExportReports();
  }, [
    isFirstRenderForExportReports,
    fetchKintoneExportReports,
    currentExportReportsPage,
  ]);

  const updateKintoneExportSetting =
    React.useCallback<UpdateKintoneExportSettingFuncType>(
      async (param, callback) => {
        try {
          const res = await runGqlQueryV2(() => {
            return gqlClient.updateKintoneExportSetting(
              {
                input: param,
              },
              gqlHeader("m"),
            );
          });

          if (res.error) {
            reportUnexpectedError(res.error.rawError);
            callback.onFailed("連携設定の更新に失敗しました。");
            return;
          }

          const newSetting = nullToUndefinedDeep(
            res.data!.updateKintoneExportSetting,
          );
          callback.onSucceeded(newSetting);
          setKintoneExportSetting(newSetting);
        } catch (error) {
          reportUnexpectedError(error);
          callback.onFailed("連携設定の更新に失敗しました。");
        }
      },
      [setKintoneExportSetting],
    );

  return {
    data:
      !!kintoneExportSetting && !!procedure
        ? {
            kintoneExportSetting,
            kintoneExportedRecords,
            kintoneExportReports,
            procedure,
          }
        : undefined,
    isLoading,
    updateKintoneExportSetting,
    currentExportedRecordsPage,
    setCurrentExportedRecordsPage,
    lastExportedRecordsPage,
    currentExportReportsPage,
    setCurrentExportReportsPage,
    lastExportReportsPage,
  };
};

const useDeleteKintoneExportedRecord = () => {
  const [deletedRecordId, setDeletedRecordId] =
    React.useState<KintoneExportedRecord["id"]>();

  const deleteKintoneExportedRecord =
    React.useCallback<DeleteKintoneExportedRecordFuncType>(
      async (param, callback) => {
        try {
          const res = await runGqlQueryV2(() => {
            return gqlClient.deleteKintoneExportedRecord(param, gqlHeader("m"));
          });

          if (res.error) {
            reportUnexpectedError(res.error.rawError);
            callback.onFailed("連携済みレコードの削除に失敗しました。");
            return;
          }

          setDeletedRecordId(param.id);
          callback.onSucceeded();
        } catch (error) {
          reportUnexpectedError(error);
          callback.onFailed("連携済みレコードの削除に失敗しました。");
        }
      },
      [setDeletedRecordId],
    );

  return {
    deletedRecordId,
    deleteKintoneExportedRecord,
  };
};

export type UseKintoneFieldsByExportSettingReturnType = {
  kintoneFormFields?: KintoneFormFields;
  errorMessage?: string;
};

export const useKintoneFieldsByExportSetting = ({
  kintoneExportSetting,
}: {
  kintoneExportSetting?: KintoneExportSetting;
}) => {
  const [kintoneFormFields, setKintoneFormFields] =
    React.useState<KintoneFormFields>();
  const [errorMessage, setErrorMessage] = React.useState<string>();

  React.useEffect(() => {
    if (!kintoneExportSetting) {
      return;
    }

    const fetchKintoneFormFields = async () => {
      try {
        const res = await runGqlQueryV2(() => {
          return gqlClient.kintoneFieldsBySettingId(
            { settingId: kintoneExportSetting.id },
            gqlHeader("q"),
          );
        });

        if (res.error) {
          throw res.error.errors.length === 1
            ? res.error.errors[0]
            : res.error.rawError;
        }

        setKintoneFormFields(res.data!.kintoneFields.properties);
        setErrorMessage(undefined);
      } catch (e) {
        if (parseError(e)?.errorCode === "API_TOKEN") {
          setErrorMessage(
            "kintone の項目情報の取得に失敗しました。APIトークンが正しく設定されているか確認してください。",
          );
          return;
        }
        reportUnexpectedError(e);
        setErrorMessage("kintone の項目情報の取得に失敗しました。");
      }
    };

    fetchKintoneFormFields();
  }, [kintoneExportSetting, setKintoneFormFields, setErrorMessage]);

  return {
    kintoneFormFields,
    errorMessage,
  };
};
